import { mergeStyles } from "@fluentui/react";
import { Dispatch, useEffect, useReducer } from "react";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import {
  IFeatureRequestFilterPillDispatchProps,
  IFeatureRequestReducerType
} from "../../interfaces/FeatureRequestView.interface";
import { CustomFilterPill } from "../../SharedComponents/CustomFilterPill";
import { useGetFeatureRequestFiltersQuery } from "../../slices/ExploreFeatureRequests.slice";
import {
  featureRequestFilterPillReducer,
  mapFeatureRequestFilterPillMenuItemsPredicate
} from "./FeatureRequestSearch.common";
import { FEATURE_REQUEST_FILTER_PILL } from "./FeatureRequestSearch.constants";

export const FeatureRequestFilterPill = (props: any) => {
  const [sourceState, sourceDispatch] = useReducer(
    featureRequestFilterPillReducer,
    FEATURE_REQUEST_FILTER_PILL.INITIAL_STATE
  );
  const [productNameState, productNameDispatch] = useReducer(
    featureRequestFilterPillReducer,
    FEATURE_REQUEST_FILTER_PILL.INITIAL_STATE
  );
  const [customerFacingStatusState, customerFacingStatusDispatch] = useReducer(
    featureRequestFilterPillReducer,
    FEATURE_REQUEST_FILTER_PILL.INITIAL_STATE
  );
  const [priorityState, priorityDispatch] = useReducer(
    featureRequestFilterPillReducer,
    FEATURE_REQUEST_FILTER_PILL.INITIAL_STATE
  );
  const { data, error } = useGetFeatureRequestFiltersQuery(null);

  const createUpdateHandler = (dispatch: Dispatch<IFeatureRequestFilterPillDispatchProps>) => (items?: string[]) => {
    dispatch({ type: IFeatureRequestReducerType.UpdateSelectedMenuItems, data: items || [] });
  };

  const onSourceUpdateHandler = createUpdateHandler(sourceDispatch);
  const onProductNameUpdateHandler = createUpdateHandler(productNameDispatch);
  const onCustomerFacingStatusUpdateHandler = createUpdateHandler(customerFacingStatusDispatch);
  const onPriorityUpdateHandler = createUpdateHandler(priorityDispatch);

  useEffect(() => {
    const customProps = {
      partnerSearchFilterPillProps: props
    };
    if (data) {
      sourceDispatch({
        type: IFeatureRequestReducerType.LoadMenuItems,
        data: ((data?.sourceNames as string[]) || []).map(mapFeatureRequestFilterPillMenuItemsPredicate)
      });
      productNameDispatch({
        type: IFeatureRequestReducerType.LoadMenuItems,
        data: ((data?.productNames as string[]) || []).map(mapFeatureRequestFilterPillMenuItemsPredicate)
      });
      customerFacingStatusDispatch({
        type: IFeatureRequestReducerType.LoadMenuItems,
        data: ((data?.customerFacingStatusNames as string[]) || []).map(mapFeatureRequestFilterPillMenuItemsPredicate)
      });
      priorityDispatch({
        type: IFeatureRequestReducerType.LoadMenuItems,
        data: ((data?.priorityNames as string[]) || []).map(mapFeatureRequestFilterPillMenuItemsPredicate)
      });
      logEvent(
        "useGetFeatureRequestFiltersQuery API call success",
        "Feature Request Search",
        "FeatureRequestFilterPill",
        customProps
      );
    }
    if (error) {
      logException(
        "Feature Request Search",
        "FeatureRequestFilterPill",
        "useGetFeatureRequestFiltersQuery",
        error,
        customProps
      );
    }
  }, [data]);

  useEffect(() => {
    let newSourceValue: string = "";
    sourceState.selectedMenuItems.forEach((element: string) => {
      newSourceValue = element + ";" + newSourceValue;
    });
    let newProductNameValue: string = "";
    productNameState.selectedMenuItems.forEach((element: string) => {
      newProductNameValue = element + ";" + newProductNameValue;
    });
    let newCustomerFacingStatusValue: string = "";
    customerFacingStatusState.selectedMenuItems.forEach((element: string) => {
      newCustomerFacingStatusValue = element + ";" + newCustomerFacingStatusValue;
    });
    let newPriorityValue: string = "";
    priorityState.selectedMenuItems.forEach((element: string) => {
      newPriorityValue = element + ";" + newPriorityValue;
    });

    props.onSourceChange(newSourceValue);
    props.onProductNameChange(newProductNameValue);
    props.onCustomerFacingStatusChange(newCustomerFacingStatusValue);
    props.onPriorityChange(newPriorityValue);
  }, [
    sourceState.selectedMenuItems,
    productNameState.selectedMenuItems,
    customerFacingStatusState.selectedMenuItems,
    priorityState.selectedMenuItems
  ]);

  return (
    <div
      className={mergeStyles({
        margin: "-4px",
        display: "flex",
        flexWrap: "wrap"
      })}
    >
      {FEATURE_REQUEST_FILTER_PILL.INITIAL_DATA.map((filterPillProps) => {
        const { key } = filterPillProps;
        const stateMapping: any = {
          source: { state: sourceState, handler: onSourceUpdateHandler },
          productName: { state: productNameState, handler: onProductNameUpdateHandler },
          customerFacingStatus: { state: customerFacingStatusState, handler: onCustomerFacingStatusUpdateHandler },
          priority: { state: priorityState, handler: onPriorityUpdateHandler }
        };

        if (key !== undefined) {
          const mapping = stateMapping[key];
          filterPillProps.menuProps = {
            focusZoneProps: {
              /* Allow up and down arrows to move focus out of the SearchBox */
              shouldInputLoseFocusOnArrowKey: () => true
            },
            items: mapping.state.menuItems
          };
          filterPillProps.onApply = mapping.handler;
          filterPillProps.onCancel = mapping.handler;
          filterPillProps.onResetFilters = () => mapping.handler([]);
        }

        return <CustomFilterPill {...filterPillProps} key={filterPillProps.key} />;
      })}
    </div>
  );
};
